import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { serverUrl } from '@utils/MkEnv'
import { fetchApi } from '@api/MkApi'
import { API } from '@constants'

// API endpoint for ownership data (using the existing account info endpoint)
const API_OWNERSHIP = `${API}/myAccount`

export interface OwnershipData {
  contracts?: Array<{
    contractNumber: string
    owners: string[]
    contractType: string
    useYearEnd: string
    pointsOwned: number
    purchaseType: string
    homeResort: string
    homeResortLocation?: string
    reciprocalResort?: string
    reciprocalResortLocation?: string
    status: string
    purchaseDate: string
    totalPoints: number
    availablePoints: number
    maintenanceFees: number
  }>
  totalPoints?: number
  availablePoints?: number
  totalCredits?: number
  availableCredits?: number
  useByDate?: string
  errorCode?: string
  errorMessage?: string
}

export interface UseOwnershipReturn {
  ownershipData: OwnershipData | null
  ownershipLoading: boolean
  ownershipError: string | null
  refreshOwnership: () => void
  resetOwnership: () => void
}

export const useOwnership = (): UseOwnershipReturn => {
  const [ownershipData, setOwnershipData] = useState<OwnershipData | null>(null)
  const [ownershipLoading, setOwnershipLoading] = useState(false)
  const [ownershipError, setOwnershipError] = useState<string | null>(null)

  const authHeaders = useAuthHeaders()
  const { isAuthorized } = useAuthentication()

  const hasFetchedRef = useRef(false)
  const jwtTokenRef = useRef<string | undefined>()

  const fetchOwnership = useCallback(async () => {
    if (!isAuthorized) {
      setOwnershipError('User not authorized')
      return
    }

    setOwnershipLoading(true)
    setOwnershipError(null)

    try {
      const url = `${serverUrl}${API_OWNERSHIP}`
      const data = await fetchApi<OwnershipData>(url, authHeaders)

      if (data) {
        setOwnershipData(data)
        hasFetchedRef.current = true
        jwtTokenRef.current = authHeaders['X-JWT-Token']
      } else {
        setOwnershipError('Failed to fetch ownership data')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch ownership data'
      setOwnershipError(errorMessage)
    } finally {
      setOwnershipLoading(false)
    }
  }, [authHeaders, isAuthorized])

  const refreshOwnership = useCallback(() => {
    hasFetchedRef.current = false
    jwtTokenRef.current = undefined
    fetchOwnership()
  }, [fetchOwnership])

  const resetOwnership = useCallback(() => {
    setOwnershipData(null)
    setOwnershipLoading(false)
    setOwnershipError(null)
    hasFetchedRef.current = false
    jwtTokenRef.current = undefined
  }, [])

  useEffect(() => {
    // Only fetch if user is authorized and we haven't fetched yet, or if the JWT token changed
    if (isAuthorized && (!hasFetchedRef.current || jwtTokenRef.current !== authHeaders?.['X-JWT-Token'])) {
      fetchOwnership()
    }

    // Reset data if user is not authorized
    if (!isAuthorized) {
      resetOwnership()
    }
  }, [isAuthorized, authHeaders?.['X-JWT-Token'], fetchOwnership, resetOwnership])

  return {
    ownershipData,
    ownershipLoading,
    ownershipError,
    refreshOwnership,
    resetOwnership,
  }
}
